import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { notificationsAPI, companiesAPI, devicesAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { formatDateTimeForTimezone } from '../utils/datetime';
import DataTable from '../components/DataTable';
import MapModal from '../components/MapModal';

export default function Notifications() {
  const { user: currentUser, timezone } = useAuthStore();
  const [notifications, setNotifications] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const defaultCompanyId = currentUser?.user_type !== 'gods_eye' ? currentUser?.company_id || '' : '';

  const [filters, setFilters] = useState({
    company_id: defaultCompanyId,
    device_id: '',
    start_date: '',
    end_date: '',
  });
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapModalData, setMapModalData] = useState({
    latitude: null,
    longitude: null,
    title: null,
    deviceBox: null,
    deviceBag: null,
    deviceSeal: null,
    userName: null,
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const companiesRes = await companiesAPI.getPublic();
      console.log('Companies fetched:', companiesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      // Don't fail if companies can't be fetched
    }
    
    try {
      const devicesRes = await devicesAPI.getPublic();
      console.log('Devices fetched:', devicesRes.data);
      setDevices(devicesRes.data);
    } catch (error) {
      console.error('Error fetching devices:', error);
      // Don't fail if devices can't be fetched - non-gods_eye users may not have DEVICES access
      setDevices([]);
    }
    
    // Load notifications initially (scoped by company for non-gods_eye users)
    fetchNotifications({});
  };

  const fetchNotifications = async (params) => {
    setLoading(true);
    try {
      const effectiveParams = { ...params };

      // For non-gods_eye users, always scope notifications to their company
      if (currentUser?.user_type !== 'gods_eye' && currentUser?.company_id && !effectiveParams.company_id) {
        effectiveParams.company_id = currentUser.company_id;
      }

      const response = await notificationsAPI.getAll(effectiveParams);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Error fetching notifications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSearch = () => {
    const params = {};
    if (filters.device_id) {
      params.device_id = parseInt(filters.device_id);
    }
    if (filters.company_id) {
      params.company_id = parseInt(filters.company_id);
    }
    if (filters.start_date) {
      params.start_date = filters.start_date;
    }
    if (filters.end_date) {
      params.end_date = filters.end_date;
    }
    fetchNotifications(params);
  };

  const handleClearFilters = () => {
    setFilters({ company_id: defaultCompanyId, device_id: '', start_date: '', end_date: '' });
    fetchNotifications({});
  };



  const getDeviceName = (deviceId) => {
    if (!deviceId) return 'System';
    const device = devices.find(d => d.id === deviceId);
    return device ? device.name : `Device #${deviceId}`;
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company #${companyId}`;
  };

  const openMapModal = (notification) => {
    setMapModalData({
      latitude: notification.latitude,
      longitude: notification.longitude,
      title: 'Scan Location',
      deviceBox: notification.device?.box || null,
      deviceBag: notification.device?.bag || null,
      deviceSeal: notification.device?.seal || null,
      userName: notification.user ? `${notification.user.first_name} ${notification.user.last_name}` : null,
    });
    setMapModalOpen(true);
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'title', label: 'Title', sortable: true },
    { key: 'message', label: 'Message', sortable: true },
    {
      key: 'device',
      label: 'Device Details',
      render: (value, row) => {
        // Use the device object from the notification if available
        if (row.device && row.device.box) {
          return `${row.device.box} / ${row.device.bag} / ${row.device.seal}`;
        }
        return `Device #${row.device_id || 'N/A'}`;
      },
      sortable: true
    },
    {
      key: 'user',
      label: 'User',
      render: (value, row) => {
        if (row.user) {
          return `${row.user.first_name} ${row.user.last_name} (${row.user.username})`;
        }
        return row.user_id ? `User #${row.user_id}` : 'System';
      },
      sortable: true
    },
    {
      key: 'notification_type',
      label: 'Type',
      render: (value) => (
        <span style={{ 
          padding: '4px 8px', 
          borderRadius: '4px',
          fontSize: '12px',
          backgroundColor: value === 'error' ? '#fee' : 
                          value === 'warning' ? '#ffeaa7' :
                          value === 'success' ? '#d4edda' : '#d1ecf1',
          color: value === 'error' ? '#c33' : 
                value === 'warning' ? '#856404' :
                value === 'success' ? '#155724' : '#0c5460'
        }}>
          {value}
        </span>
      ),
      sortable: true
    },
    {
      key: 'location',
      label: 'Location',
      render: (value, row) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {row.ip_address && <div>IP: {row.ip_address}</div>}
            {row.latitude && row.longitude && (
              <button 
                onClick={() => openMapModal(row)}
                style={{ 
                  color: '#007bff', 
                  textDecoration: 'none', 
                  fontWeight: '500',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  font: 'inherit',
                  textAlign: 'left'
                }}
                title="View on interactive map"
              >
                View on Map ({row.latitude.toFixed(4)}, {row.longitude.toFixed(4)})
              </button>
            )}
          </div>
        );
      },
      width: '280px'
    },
    {
      key: 'company_id',
      label: 'Company',
      render: (value, row) => {
        if (row.company) {
          return row.company.name;
        }
        return getCompanyName(value);
      },
      sortable: true
    },
    {
      key: 'created_at',
      label: 'Created At',
      render: (value) => formatDateTimeForTimezone(value, timezone),
      sortable: true
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-sm btn-info"
            onClick={() => showNotificationDetails(row)}
            title="View Details"
          >
            Details
          </button>

        </div>
      ),
      width: '160px'
    }
  ];

  const showNotificationDetails = (notification) => {
    setSelectedNotification(notification);
    setShowDetailsModal(true);
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '8px' }}>
        <h1 className="page-title">Notifications</h1>
      </div>

      <div className="filter-section">
      <h3>Filter Notifications</h3>
        
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Company:</label>
            <select
              name="company_id"
              value={filters.company_id}
              onChange={handleFilterChange}
              disabled={currentUser?.user_type !== 'gods_eye'}
            >
              {currentUser?.user_type === 'gods_eye' && <option value="">-- All Companies --</option>}
              name="device_id"
              value={filters.device_id}
              onChange={handleFilterChange}
            >
              <option value="">-- All Devices --</option>
              {devices
                .filter(device => currentUser?.user_type === 'gods_eye' || device.company_id === currentUser?.company_id)
                .map((device) => (
                <option key={device.id} value={device.id}>
                  {device.box} - {device.bag} - {device.seal}
                  {currentUser?.user_type === 'gods_eye' && ` (${device.uid})`}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Start Date:</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>End Date:</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Search:</label>
            <input
              type="text"
              name="search"
              placeholder="Search text..."
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button className="btn btn-secondary" onClick={handleClearFilters}>
            Reset Filters
          </button>
        </div>

          <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
            💡 Tip: Filter by company to see all notifications from devices in that company, or select a specific device
          </p>
      </div>

      {loading ? (
        <p>Loading notifications...</p>
      ) : notifications.length === 0 ? (
        <div style={{ backgroundColor: '#e8f4f8', padding: '20px', borderRadius: '4px', textAlign: 'center' }}>
          <p>No notifications found</p>
          {(filters.company_id || filters.device_id) && (
            <p style={{ fontSize: '12px', color: '#666' }}>Try adjusting your filters</p>
          )}
        </div>
      ) : (
          <DataTable
            data={notifications}
            columns={columns}
            searchableFields={['title', 'message']}
            itemsPerPage={10}
            title="Notifications List"
          />
        )}
      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '4px', borderLeft: '4px solid #3498db' }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
          <strong>Total notifications shown:</strong> {notifications.length}
        </p>
      </div>

      {/* Notification Details Modal */}
      {showDetailsModal && selectedNotification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: '#333' }}>Notification Details</h3>
            
            <div style={{ display: 'grid', gap: '15px' }}>
              <div className="detail-group">
                <strong>ID:</strong> {selectedNotification.id}
              </div>
              <div className="detail-group">
                <strong>Title:</strong> {selectedNotification.title}
              </div>
              <div className="detail-group">
                <strong>Message:</strong> {selectedNotification.message}
              </div>
              <div className="detail-group">
                <strong>Type:</strong> 
                <span style={{ 
                  marginLeft: '8px',
                  padding: '4px 8px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  backgroundColor: selectedNotification.notification_type === 'error' ? '#fee' : 
                                  selectedNotification.notification_type === 'warning' ? '#ffeaa7' :
                                  selectedNotification.notification_type === 'success' ? '#d4edda' : '#d1ecf1'
                }}>
                  {selectedNotification.notification_type}
                </span>
              </div>
              
              {selectedNotification.device && (
                <div className="detail-group">
                  <strong>Device:</strong> {selectedNotification.device.box} / {selectedNotification.device.bag} / {selectedNotification.device.seal}
                  {currentUser?.user_type === 'gods_eye' && (
                    <>
                      <br />
                      <small>UID: {selectedNotification.device.uid}</small>
                    </>
                  )}
                </div>
              )}
              
              {selectedNotification.user && (
                <div className="detail-group">
                  <strong>User:</strong> {selectedNotification.user.first_name} {selectedNotification.user.last_name}
                  <br />
                  <small>Username: {selectedNotification.user.username} | Email: {selectedNotification.user.email} | Type: {selectedNotification.user.user_type}</small>
                </div>
              )}
              
              {selectedNotification.company && (
                <div className="detail-group">
                  <strong>Company:</strong> {selectedNotification.company.name}
                </div>
              )}
              
              <div className="detail-group">
                <strong>Location & Network:</strong>
                {selectedNotification.ip_address && (
                  <div>📡 IP Address: {selectedNotification.ip_address}</div>
                )}
                {selectedNotification.latitude && selectedNotification.longitude && (
                  <div>
                    <a 
                      href={`https://maps.google.com/?q=${selectedNotification.latitude},${selectedNotification.longitude}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none', fontWeight: '500' }}
                      title="Open in Google Maps"
                    >
                      🗺️ View on Google Maps
                    </a>
                    <br />
                    <small>📍 Coordinates: {selectedNotification.latitude.toFixed(6)}, {selectedNotification.longitude.toFixed(6)}</small>
                  </div>
                )}
                {!selectedNotification.ip_address && !selectedNotification.latitude && (
                  <div>No location data available</div>
                )}
              </div>
              
              <div className="detail-group">
                <strong>Status:</strong> {selectedNotification.is_read ? '✅ Read' : '❌ Unread'}
              </div>
              
              <div className="detail-group">
                <strong>Created:</strong>{' '}
                {formatDateTimeForTimezone(selectedNotification.created_at, timezone)}
              </div>
              
              {selectedNotification.context_data && (
                <div className="detail-group">
                  <strong>Additional Data:</strong>
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '10px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(selectedNotification.context_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal for viewing scan location */}
      <MapModal
        isOpen={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        latitude={mapModalData.latitude}
        longitude={mapModalData.longitude}
        title={mapModalData.title}
        deviceBox={mapModalData.deviceBox}
        deviceBag={mapModalData.deviceBag}
        deviceSeal={mapModalData.deviceSeal}
        userName={mapModalData.userName}
      />
    </div>
  );
}
